import { useState } from 'react';
import { AutoComplete, Input, Spin } from 'antd';
import { CiSearch } from 'react-icons/ci';
import { FaRoad, FaTrafficLight } from 'react-icons/fa';
import '../SemafixPage.css';

/**
 * Semafix v2 §2 — compact top-right search.
 *
 * Searches both map streets (highlighting the matched one and dimming the
 * rest, like AssignX) and generic locations (Nominatim). The map filters now
 * live in the interactive legend (SemafixLegend), so this bar is search-only.
 */
export default function SemafixFilterBar({
  roads = [],
  intersections = [],
  onLocationSelect,
  onRoadSelect,
  onIntersectionSelect,
}) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const roadMatches = (value) => {
    const term = value.trim().toLowerCase();
    if (!term) return [];
    return roads
      .filter((road) => road.n && road.n.toLowerCase().includes(term))
      // Unique by name so the dropdown is not flooded with segments.
      .reduce((acc, road) => (acc.some((r) => r.n === road.n) ? acc : [...acc, road]), [])
      .slice(0, 5)
      .map((road) => ({
        value: `road:${road.id}`,
        road,
        label: (
          <div className="semafix-location-suggest-item">
            <div className="semafix-suggestion-title">
              <FaRoad style={{ marginRight: 6, color: '#A1E430' }} />
              {road.n}
            </div>
            <div className="semafix-suggestion-subtitle">Strada nella rete</div>
          </div>
        ),
      }));
  };

  const intersectionMatches = (value) => {
    const term = value.trim().toLowerCase();
    if (!term) return [];
    return intersections
      .filter((item) => item.name?.toLowerCase().includes(term))
      .slice(0, 5)
      .map((intersection) => ({
        value: `signal:${intersection.id}`,
        intersection,
        label: (
          <div className="semafix-location-suggest-item">
            <div className="semafix-suggestion-title">
              <FaTrafficLight style={{ marginRight: 6, color: '#A1E430' }} />
              {intersection.name}
            </div>
            <div className="semafix-suggestion-subtitle">Intersezione semaforizzata</div>
          </div>
        ),
      }));
  };

  const searchLocation = async (value) => {
    const roadOptions = [...intersectionMatches(value), ...roadMatches(value)];
    if (!value || value.length < 3) {
      setOptions(roadOptions);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=5`,
      );
      const data = await response.json();
      const locationOptions = data.map((item) => ({
        value: item.display_name,
        coordinates: [parseFloat(item.lat), parseFloat(item.lon)],
        label: (
          <div className="semafix-location-suggest-item">
            <div className="semafix-suggestion-title">{item.display_name.split(',')[0]}</div>
            <div className="semafix-suggestion-subtitle">
              {item.display_name.split(',').slice(1).join(',').trim()}
            </div>
          </div>
        ),
      }));
      setOptions([...roadOptions, ...locationOptions]);
    } catch (error) {
      console.error('Error searching location:', error);
      setOptions(roadOptions);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (value) => {
    setQuery(value);
    searchLocation(value);
  };

  const handleSelect = (value, option) => {
    if (option?.road) {
      onRoadSelect?.(option.road);
    } else if (option?.intersection) {
      onIntersectionSelect?.(option.intersection);
    } else if (option?.coordinates) {
      onLocationSelect?.({ label: value, coordinates: option.coordinates });
    }
  };

  return (
    <div className="semafix-filter-bar">
      <div className="semafix-filter-input-container">
        <AutoComplete
          value={query}
          options={options}
          onSelect={handleSelect}
          onSearch={handleSearch}
          onClear={() => { setQuery(''); setOptions([]); onRoadSelect?.(null); }}
          className="semafix-search-autocomplete"
          notFoundContent={searchLoading ? <Spin size="small" /> : null}
          dropdownClassName="semafix-location-suggest-dropdown"
        >
          <Input
            placeholder="Cerca strada o posizione…"
            prefix={<CiSearch className="semafix-search-icon" />}
            className="semafix-filter-input"
            allowClear
          />
        </AutoComplete>
      </div>
    </div>
  );
}
